// "Start": access, installing the CLI, the first wiki, the first capsule, the web app — then
// what is planned. Sources: apps/web/README.md (closed beta, access keys, Ordinal Pass off until
// configured); DR-210 A + the CLI package (WP-C15: wheel `qodoonect_collect-<version>-py3-none-
// any.whl` sent to testers, `pipx install`, console command `collect`, default server = this site,
// so only COLLECT_API_KEY is needed; the workspace folder name must be the one the key is bound
// to); `--encrypt` and `keys` are experimental (DR-210) and never the first-capsule path;
// docs/spec/COLLECT-MVP-SPEC.md §8 (roadmap: browser-side ML-KEM decryption, Tier 2
// Cloud-Private), apps/extension/README.md (Chrome Web Store), apps/desktop/src-tauri/
// tauri.conf.json (no signing key yet).
import { motion } from "motion/react";
import { LogIn } from "lucide-react";
import { useLandingTheme } from "./theme";

/** One terminal line; `ps` = a PowerShell line (prompt "PS>"), else a POSIX shell line ("$"). */
type Line = { c: string; note?: string; ps?: boolean };

// `./` makes pipx read the argument as a local file; without it, it is parsed as a PEP 508
// requirement and the `[all]` extras are dropped (CLI package README, WP-C15 / #285).
const INSTALL: Line[] = [
  { c: 'pipx install "./qodoonect_collect-<version>-py3-none-any.whl[all]"', note: "in the folder where you saved the wheel" },
  { c: "collect --version" },
];

// Each `export` has its PowerShell twin (`ps`), as in the CLI README.
const WIKI: Line[] = [
  { c: "collect init my-wiki", note: "use the workspace name you were given" },
  { c: "export ANTHROPIC_API_KEY=…", note: "or OPENAI_API_KEY / OPENROUTER_API_KEY" },
  { c: '$env:ANTHROPIC_API_KEY = "…"', note: "the same in PowerShell", ps: true },
  { c: "collect ingest my-wiki notes.md" },
  { c: 'collect search my-wiki "your topic"' },
];

const SEND: Line[] = [
  { c: "export COLLECT_API_KEY=…", note: "the access key you were given" },
  { c: '$env:COLLECT_API_KEY = "…"', note: "the same in PowerShell", ps: true },
  { c: "collect send-to-hive my-wiki" },
];

const SEAL: Line[] = [
  { c: "collect keys init my-wiki", note: "experimental · asks for a passphrase" },
  { c: "collect send-to-hive my-wiki --encrypt", note: "experimental" },
];

/** A narrow screen wraps a command between words, never inside a flag ("-⏎-encrypt"). */
function flagsWhole(c: string) {
  return c.split(/(--[\w-]+)/).map((part, i) => (i % 2 ? <span key={i} className="whitespace-nowrap">{part}</span> : part));
}

function Terminal({ label, lines }: { label: string; lines: Line[] }) {
  return (
    <figure className="collect-code rounded-xl p-4 md:p-5 min-w-0">
      <figcaption className="collect-code-label font-mono text-[10px] uppercase tracking-[0.25em] mb-3">{label}</figcaption>
      <pre className="m-0 font-mono text-[12px] md:text-[13px] leading-relaxed whitespace-pre-wrap break-words">
        {lines.map((l) => (
          <span key={l.c} className="block">
            <span className="collect-code-prompt" aria-hidden="true">{l.ps ? "PS> " : "$ "}</span>
            {flagsWhole(l.c)}
            {l.note && <span className="collect-code-note">{"  # "}{l.note}</span>}
          </span>
        ))}
      </pre>
    </figure>
  );
}

export function StartSection({ passSignIn, signInLabel, onSignIn }: { passSignIn: boolean; signInLabel: string; onSignIn: () => void }) {
  const { tone } = useLandingTheme();
  const planned = [
    "Desktop app release (Windows, macOS, Linux)",
    "Browser extension in the Chrome Web Store",
    "Opening encrypted capsules in the browser",
    "Cloud-Private: a self-hosted HIVE on your own VM",
    ...(passSignIn ? [] : ["Sign-in with Ordinal Pass"]),
  ];
  const steps = [
    {
      title: "Get access",
      body: passSignIn
        ? "Collect is in closed beta. Sign in with your Ordinal Pass at Qodoonect Trust; the Qodoonect team sends you the command-line tool as a wheel file."
        : "Collect is in closed beta. The Qodoonect team sends you the command-line tool as a wheel file, an access key and the workspace name that key is bound to.",
    },
    { title: "Install the CLI", body: "Python 3.10 or newer, pipx and git.", terminal: { label: "Terminal · install", lines: INSTALL } },
    { title: "Build a wiki", body: "Your LLM key, your documents.", terminal: { label: "Terminal · a first wiki", lines: WIKI } },
    {
      title: "Send a capsule",
      body: "The capsule is gzip-compressed and SHA-256 hashed — not encrypted.",
      terminal: { label: "Terminal · a first capsule", lines: SEND },
      extra: {
        note: "Optional and experimental (CLI only): seal the capsule with post-quantum encryption. Keep the passphrase and keys/dk.enc — losing either makes encrypted capsules unreadable.",
        terminal: { label: "Terminal · experimental (CLI)", lines: SEAL },
      },
    },
    { title: "Open it here", body: "Sign in to see your capsules, read them, explore the knowledge graph and compare versions." },
  ];
  return (
    <section id="start" aria-labelledby="start-title" className="px-6 md:px-12 py-24 md:py-32 border-t border-white/10">
      <div className="max-w-7xl mx-auto">
        <header className="mb-16">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/60 mb-4">Get started</p>
          <h2 id="start-title" className="text-5xl md:text-7xl font-serif font-light leading-[0.95]">
            {passSignIn ? <>Your pass.<br /><span className="italic text-white/65">Your HIVE.</span></> : <>Closed beta.<br /><span className="italic text-white/65">By invitation.</span></>}
          </h2>
        </header>

        <div className="grid gap-10 lg:grid-cols-[1.5fr_1fr] lg:gap-16 items-start">
          <ol className="grid gap-8">
            {steps.map((s, i) => (
              <motion.li
                key={s.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="grid grid-cols-[2.5rem_1fr] gap-x-4"
              >
                <span className="font-mono text-xs tracking-[0.3em] pt-1.5" style={{ color: tone("#10B981") }}>0{i + 1}</span>
                <div className="min-w-0">
                  <h3 className="text-2xl md:text-3xl font-serif font-light mb-2">{s.title}</h3>
                  <p className="text-sm md:text-base font-light text-white/70 leading-relaxed">{s.body}</p>
                  {s.terminal && <div className="mt-4"><Terminal {...s.terminal} /></div>}
                  {s.extra && (
                    <div className="mt-5">
                      <p className="text-xs md:text-sm font-light text-white/60 leading-relaxed mb-3">
                        <span className="collect-tag mr-2" style={{ color: tone("#F2C94C"), borderColor: "#F2C94C55" }}>Experimental</span>
                        {s.extra.note}
                      </p>
                      <Terminal {...s.extra.terminal} />
                    </div>
                  )}
                  {i === steps.length - 1 && (
                    <button type="button" className="btn btn-primary mt-5" onClick={onSignIn}>
                      <LogIn size={16} aria-hidden="true" />
                      {signInLabel}
                    </button>
                  )}
                </div>
              </motion.li>
            ))}
          </ol>

          <motion.aside
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            aria-labelledby="planned-title"
            className="glass-panel rounded-2xl p-7 md:p-8 lg:sticky lg:top-28"
          >
            <h3 id="planned-title" className="font-mono text-xs uppercase tracking-[0.3em] text-white/60 mb-6">Planned</h3>
            <ul className="grid gap-4">
              {planned.map((p) => (
                <li key={p} className="flex items-start justify-between gap-4 text-sm font-light text-white/80">
                  <span>{p}</span>
                  <span className="collect-tag flex-none" style={{ color: tone("#F2C94C"), borderColor: "#F2C94C55" }}>Planned</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-xs font-light text-white/60 leading-relaxed">
              Planned means not built or not released yet. No dates promised.
            </p>
          </motion.aside>
        </div>
      </div>
    </section>
  );
}
